import { Color, Typography } from '@persona-id/design-tokens';
import { motion } from 'framer-motion';
import { sortBy } from 'lodash/fp';
import { useMemo } from 'react';
import { Controller, useFormState } from 'react-hook-form';
import { FormError } from 'shared/components/FormControl';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import Form from 'dashboard/layouts/FormLayout/Form';
import Button from 'design-system/components/Button';
import { Input } from 'design-system/components/Input';
import MaskedInput from 'design-system/components/MaskedInput';
import Select, { Option } from 'design-system/components/Select';
import Spacer from 'design-system/components/Spacer';
import { Label, Paragraph } from 'design-system/components/Text';
import TextArea from 'design-system/components/TextArea';
import { COUNTRIES } from 'lib/constants/locales';
import { EMPLOYER_IDENTIFICATION_NUMBER_REGEX } from 'lib/constants/regexes';
import { useFormSecurely } from 'lib/security/reactHookForm';

export interface FormRegisterTrialEssentialProps {
  submit: (data: FormRegisterTrialEssentialData) => void;
  skipTrialEssential: () => void;
}

const SkipTrialEssentialLink = styled('span')`
  color: ${Color.CoreSlate900};
  ${Typography.TextMediumLight};
  display: block;
  text-align: center;
  text-decoration: underline;
  cursor: pointer;
`;

export const FormRegisterTrialEssential = ({
  submit,
  skipTrialEssential,
}: FormRegisterTrialEssentialProps) => {
  const { control, handleSubmit, register, watch, unregister } =
    useFormSecurely<FormRegisterTrialEssentialData>();
  const { isSubmitting, errors } = useFormState({
    control,
  });

  const countries = Object.keys(COUNTRIES) as Array<keyof typeof COUNTRIES>;
  const countryCodeOptions = sortBy(
    'label',
    countries.map((countryCode) => ({
      label: COUNTRIES[countryCode],
      value: countryCode,
    })),
  ).filter((object) => object.value !== 'US');
  // We unshift the US option back onto the top of the list to ensure its first
  countryCodeOptions.unshift({ value: 'US', label: COUNTRIES['US'] });

  const businessCountryCode = watch('business-country-code');

  // This is wrapped in useMemo to allow us to unregister the field when the country changes. If unregister is called
  // outside of the memo it infinite loops b/c it causes a re-render, which then calls `unregister` again, etc.
  const inputMemo = useMemo(() => {
    unregister('business-identification-number');

    if (businessCountryCode === 'US') {
      return (
        <>
          <Label htmlFor='business-identification-number' size='md' weight='bold'>
            Employer Identification Number (EIN)
          </Label>
          <Spacer height={1} />
          <MaskedInput
            {...register('business-identification-number', {
              required: 'Please enter your Employer Identification Number (EIN)',
              pattern: {
                value: EMPLOYER_IDENTIFICATION_NUMBER_REGEX,
                message: 'Please enter a valid Employer Identification Number (EIN)',
              },
            })}
            id='business-identification-number'
            data-test='form-register__business-identification-number'
            mask='00-0000000'
            placeholder='##-#######'
          />
        </>
      );
    } else if (businessCountryCode != null) {
      return (
        <>
          <Label htmlFor='business-identification-number' size='md' weight='bold'>
            Business registration or identification number
          </Label>
          <Spacer height={1} />
          <Input
            {...register('business-identification-number', {
              required: 'Please enter your business registration or identification number',
              validate: {
                notEmpty: (value) => {
                  if (value != null && value.trim() === '') {
                    return 'Please enter a valid business registration or identification number';
                  }
                },
              },
            })}
            id='business-identification-number'
            data-test='form-register__business-identification-number'
            placeholder='Please enter your business registration or identification number'
          />
        </>
      );
    }
    // We disable the exhaustive dependencies checks b/c we do not want this to update when the `register` and
    // `unregister` functions are updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [businessCountryCode]);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1 }}>
      <Spacer height={1} />
      <Paragraph size='md' color='primary'>
        Based on the information you provided, you can request a free trial of Persona with live
        verification services.
      </Paragraph>
      <br />
      <Paragraph size='md' color='primary'>
        With live verification services, you can try Persona in a Production environment. This means
        you can input real data (including documents & ID cards), see actual extractions, and get
        real verification results.
      </Paragraph>
      <Divider full />
      <Paragraph size='md' color='primary'>
        To proceed, we’ll need the information below to verify your business:
      </Paragraph>
      <Spacer height={1} />
      <Form onSubmit={handleSubmit(submit)}>
        <Label htmlFor='business-country-code' size='md' weight='bold'>
          Primary country of operation
        </Label>
        <Spacer height={1} />
        <Controller
          name='business-country-code'
          control={control}
          rules={{ required: 'Please enter the primary country of operation' }}
          render={({ field }) => (
            <Select
              id='business-country-code'
              data-test='trial-form-register__business-country-code'
              placeholder='Select country of business registration or headquarters...'
              {...field}
            >
              {countryCodeOptions.map((opt) => (
                <Option key={opt.value} value={opt.value}>
                  {opt.label}
                </Option>
              ))}
            </Select>
          )}
        />
        {errors['business-country-code'] && (
          <FormError>{errors['business-country-code'].message}</FormError>
        )}
        <Spacer height={2} />
        {inputMemo}
        {errors['business-identification-number'] && (
          <FormError>{errors['business-identification-number'].message}</FormError>
        )}
        <Spacer height={2} />
        <Label htmlFor='use-case-description' size='md' weight='bold'>
          Describe how you plan to use Persona
        </Label>
        <Spacer height={1} />
        <TextArea
          {...register('use-case-description', {
            required: 'Please describe how you plan to use Persona.',
            maxLength: { value: 1024, message: 'Please limit description to 1024 characters' },
            validate: {
              notEmpty: (value) => {
                if (value != null && value.trim() === '') {
                  return 'Please describe how you plan to use Persona.';
                }
              },
            },
          })}
          placeholder='Briefly describe your use case or the challenge you’d like to address with Persona. This helps us determine your eligibility for a trial.'
          id='use-case-description'
          data-test='trial-form-register__use-case-description'
        />
        {errors['use-case-description'] && (
          <FormError>{errors['use-case-description'].message}</FormError>
        )}
        <Spacer height={4} />
        <Button
          data-test='form-register__submit'
          type='submit'
          disabled={isSubmitting}
          isPending={isSubmitting}
        >
          Get started on a trial with Production access
        </Button>
      </Form>
      <Spacer height={2} />
      <SkipTrialEssentialLink id='form-register__skip-trial-essential' onClick={skipTrialEssential}>
        Get started with Sandbox access only
      </SkipTrialEssentialLink>
    </motion.div>
  );
};

export interface FormRegisterTrialEssentialData {
  'business-identification-number': string;
  'business-country-code': string;
  'use-case-description': string;
}

export default FormRegisterTrialEssential;
